import { useEffect, useState } from 'react';
import { useToast } from '../../context/ToastContext';

const ToastContainer = () => {
  const { toasts, removeToast } = useToast();
  const [loadingToasts, setLoadingToasts] = useState(new Set());

  // Handle Escape key to dismiss toasts
  useEffect(() => {
    const handleEscapeKey = (event) => {
      if (event.key === 'Escape' && toasts.length > 0) {
        // Dismiss the most recent toast (last in array)
        const latestToast = toasts[toasts.length - 1];

        // Don't dismiss if any toast is loading
        if (loadingToasts.has(latestToast.id)) {
          return;
        }

        if (latestToast.onCancel) {
          latestToast.onCancel(); // Trigger cancel callback for confirmation toasts
        }
        removeToast(latestToast.id);
      }
    };

    document.addEventListener('keydown', handleEscapeKey);
    return () => document.removeEventListener('keydown', handleEscapeKey);
  }, [toasts, removeToast, loadingToasts]);

  const getToastTypeConfig = (type) => {
    switch (type) {
      case 'success':
        return {
          icon: (
            <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
              <path d="M10 .5a9.5 9.5 0 1 0 9.5 9.5A9.51 9.51 0 0 0 10 .5Zm3.707 8.207-4 4a1 1 0 0 1-1.414 0l-2-2a1 1 0 0 1 1.414-1.414L9 10.586l3.293-3.293a1 1 0 0 1 1.414 1.414Z"/>
            </svg>
          ),
          iconBg: 'bg-green-100 text-green-600 dark:bg-green-800 dark:text-green-200',
          title: 'Success'
        };
      case 'error':
        return {
          icon: (
            <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
              <path d="M10 .5a9.5 9.5 0 1 0 9.5 9.5A9.51 9.51 0 0 0 10 .5Zm3.707 11.793a1 1 0 1 1-1.414 1.414L10 11.414l-2.293 2.293a1 1 0 0 1-1.414-1.414L8.586 10 6.293 7.707a1 1 0 0 1 1.414-1.414L10 8.586l2.293-2.293a1 1 0 0 1 1.414 1.414L11.414 10l2.293 2.293Z"/>
            </svg>
          ),
          iconBg: 'bg-red-100 text-red-600 dark:bg-red-800 dark:text-red-200',
          title: 'Error'
        };
      case 'warning':
        return {
          icon: (
            <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
              <path d="M10 .5a9.5 9.5 0 1 0 9.5 9.5A9.51 9.51 0 0 0 10 .5ZM10 15a1 1 0 1 1 0-2 1 1 0 0 1 0 2Zm1-4a1 1 0 0 1-2 0V6a1 1 0 0 1 2 0v5Z"/>
            </svg>
          ),
          iconBg: 'bg-orange-100 text-orange-600 dark:bg-orange-800 dark:text-orange-200',
          title: 'Warning'
        };
      default:
        return {
          icon: (
            <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
              <path d="M10 .5a9.5 9.5 0 1 0 9.5 9.5A9.51 9.51 0 0 0 10 .5ZM9.5 4a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3ZM12 15H8a1 1 0 0 1 0-2h1v-3H8a1 1 0 0 1 0-2h2a1 1 0 0 1 1 1v4h1a1 1 0 0 1 0 2Z"/>
            </svg>
          ),
          iconBg: 'bg-blue-100 text-blue-600 dark:bg-blue-800 dark:text-blue-200',
          title: 'Information'
        };
    }
  };

  if (toasts.length === 0) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
      {/* Modal backdrop */}
      <div className="absolute inset-0 bg-black bg-opacity-50 dark:bg-opacity-70"></div>

      <div className="relative space-y-4 w-full max-w-md">
        {toasts.map((toast) => {
          const typeConfig = getToastTypeConfig(toast.type);

          return (
            <div
              key={toast.id}
              className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl border border-gray-200 dark:border-gray-600 overflow-hidden animate-in slide-in-from-top-4 fade-in duration-300"
            >
              {/* Title Bar - Same color as navbar */}
              <div className="bg-gray-200 dark:bg-gray-700 px-4 py-3 border-b border-gray-300 dark:border-gray-600 flex items-center">
                <div className={`inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full ${typeConfig.iconBg} mr-3`}>
                  {typeConfig.icon}
                </div>
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white flex-1">
                  {toast.title || typeConfig.title}
                </h3>
                <button
                  onClick={() => {
                    if (toast.onCancel) toast.onCancel();
                    removeToast(toast.id);
                  }}
                  disabled={loadingToasts.has(toast.id)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 disabled:text-gray-300 disabled:cursor-not-allowed ml-2 p-1 rounded-full hover:bg-gray-300 dark:hover:bg-gray-600 disabled:hover:bg-transparent transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Body */}
              <div className="px-4 py-4">
                <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
                  {toast.message}
                </p>
              </div>

              {/* Actions Bar */}
              {toast.showActions && (
                <div className="bg-white dark:bg-gray-800 px-3 py-2 border-t border-gray-200 dark:border-gray-600 flex justify-end">
                  <button
                    onClick={async () => {
                      if (toast.onProceed) {
                        setLoadingToasts(prev => new Set(prev.add(toast.id)));
                        try {
                          await toast.onProceed();
                        } finally {
                          setLoadingToasts(prev => {
                            const newSet = new Set(prev);
                            newSet.delete(toast.id);
                            return newSet;
                          });
                          removeToast(toast.id);
                        }
                      } else {
                        removeToast(toast.id);
                      }
                    }}
                    disabled={loadingToasts.has(toast.id)}
                    className="px-3 py-1.5 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 rounded focus:ring-2 focus:ring-blue-500 transition-colors flex items-center justify-center space-x-1 min-w-16"
                  >
                    {loadingToasts.has(toast.id) ? (
                      <>
                        <svg className="animate-spin h-3 w-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <span>{toast.loadingText || 'Loading...'}</span>
                      </>
                    ) : (
                      <span>{toast.buttonText || 'OK'}</span>
                    )}
                  </button>
                </div>
              )}

            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ToastContainer;